import { ChevronRight, ChevronDown, BookOpen } from 'lucide-react';
import { useState } from 'react';
import type { TOCItem, NavigationCallbacks } from '../types';
import styles from '../ReaderPanels.module.css';
import { useTranslation } from '../../../i18n/useTranslation';

interface TOCPanelProps {
  items: TOCItem[];
  onNavigate: NavigationCallbacks['onNavigateToTOC'];
  currentLocation?: string | number;
}

/**
 * TOCTreeItem
 *
 * Recursive component for rendering nested table of contents items.
 * Supports expand/collapse and highlights the current location.
 */
function TOCTreeItem({
  item,
  onNavigate,
  currentLocation,
  level = 0,
}: {
  item: TOCItem;
  onNavigate: NavigationCallbacks['onNavigateToTOC'];
  currentLocation?: string | number;
  level?: number;
}) {
  const [isExpanded, setIsExpanded] = useState(level === 0); // Top-level items expanded by default
  const hasChildren = item.children && item.children.length > 0;

  const isActive =
    currentLocation !== undefined &&
    (item.pageNumber === currentLocation || item.cfi === currentLocation);

  const handleClick = () => {
    if (hasChildren) {
      setIsExpanded(!isExpanded);
    }
    onNavigate(item);
  };

  return (
    <li className={`${styles.tocItem} ${level > 0 ? styles.nested : ''}`}>
      <button
        onClick={handleClick}
        className={`${styles.tocButton} ${isActive ? styles.active : ''}`}
        aria-label={`${item.label}${item.pageNumber ? `, page ${item.pageNumber}` : ''}`}
        aria-expanded={hasChildren ? isExpanded : undefined}
      >
        {hasChildren && (
          <span>
            {isExpanded ? (
              <ChevronDown style={{ width: '16px', height: '16px' }} />
            ) : (
              <ChevronRight style={{ width: '16px', height: '16px' }} />
            )}
          </span>
        )}
        <span className={styles.tocTitle}>{item.label}</span>
        {item.pageNumber !== undefined && (
          <span className={styles.tocPageNumber}>p. {item.pageNumber + 1}</span>
        )}
      </button>

      {hasChildren && isExpanded && (
        <ul className={styles.tocList}>
          {item.children!.map((child) => (
            <TOCTreeItem
              key={child.id}
              item={child}
              onNavigate={onNavigate}
              currentLocation={currentLocation}
              level={level + 1}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * TOCPanel
 *
 * Table of Contents panel component.
 * Displays hierarchical book structure with navigation.
 */
export default function TOCPanel({ items, onNavigate, currentLocation }: TOCPanelProps) {
  const { t } = useTranslation();
  if (!items || items.length === 0) {
    return (
      <div className={styles.panelContainer} style={{ width: '100%', padding: '20px' }}>
        <div className={styles.panelBody} style={{ padding: 0 }}>
          <div className={styles.emptyState}>
            <BookOpen className={styles.emptyStateIcon} />
            <h3 className={styles.emptyStateTitle}>{t('App.Reader.TOC.EmptyTitle')}</h3>
            <p className={styles.emptyStateDescription}>
              {t('App.Reader.TOC.EmptyDescription')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.panelContainer} style={{ width: '100%', padding: '20px' }}>
      {/* TOC Tree */}
      <div className={styles.panelBody} style={{ padding: 0 }}>
        <ul className={styles.tocList} role="list">
          {items.map((item) => (
            <TOCTreeItem
              key={item.id}
              item={item}
              onNavigate={onNavigate}
              currentLocation={currentLocation}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}
